// src/pages/AdsSettings.jsx
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import ConfirmModal from '../components/utils/ConfirmModal';
import FloatingFooter from '../components/utils/FloatingFooter';
import InputWithCounter from '../components/utils/InputWithCounter';
import AppContext from '../contexts/AppContext';
import ImageUploader from '../components/utils/ImageUploader';
import { message as antdMessage, Switch } from 'antd';

const AdsSettings = () => {
  const { userInfo, selectedRole, token } = useContext(AppContext);
  const advList = userInfo?.roleInfo?.adv || [];
  const initialLanding = advList.find(a => a.id === selectedRole.id);

  const [editedLanding, setEditedLanding] = useState(null);
  const [initialEditedLanding, setInitialEditedLanding] = useState(null);
  const [specialDates, setSpecialDates] = useState([]);
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tab, setTab] = useState('image'); // 'image' or 'specials'

  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;

  const getToday = () => new Date().toISOString().split('T')[0];
  const toStartOfDay = (dateStr) => new Date(dateStr + 'T00:00:00.000');
  const toEndOfDay = (dateStr) => new Date(dateStr + 'T23:59:59.999');



  // Initialize editedLanding and specialDates
  useEffect(() => {
    if (!initialLanding) return;
    const clone = {
      ...initialLanding,
      specials: initialLanding.specials || [],
      image: initialLanding.image || '',
      alias: initialLanding.alias || ''
    };
    setEditedLanding(clone);
    setInitialEditedLanding(clone);
    setSpecialDates(
      clone.specials.map(s => ({
        startDate: s.startDatetime?.split('T')[0] || getToday(),
        endDate: s.endDatetime?.split('T')[0] || getToday()
      }))
    );
  }, [initialLanding]);

  const hasChanges = useMemo(
    () => JSON.stringify(editedLanding) !== JSON.stringify(initialEditedLanding),
    [editedLanding, initialEditedLanding]
  );

  // Validate specials completeness and prevent date overlap
  useEffect(() => {
    if (!editedLanding) return;
    const specials = editedLanding.specials;
    // const allValid = specials.length > 0 && specials.every(s =>
    //   s.image &&
    //   s.startDatetime &&
    //   s.endDatetime &&
    //   new Date(s.startDatetime) <= new Date(s.endDatetime)
    // );
    const overlap = specials.some((a, i) =>
      specials.some((b, j) =>
        i !== j &&
        new Date(a.startDatetime) <= new Date(b.endDatetime) &&
        new Date(b.startDatetime) <= new Date(a.endDatetime)
      )
    );
    setMessage(overlap ? 'Date ranges overlap, please adjust.' : '');
  }, [editedLanding]);

  // const handleFileChange = async (field, file) => {
  //   const url = await uploadImage(file, 960, 540);
  //   setEditedLanding(prev => {
  //     const next = { ...prev };
  //     if (field === 'image') {
  //       next.image = url;
  //     } else {
  //       const idx = parseInt(field.split('_')[1], 10);
  //       next.specials[idx] = next.specials[idx] || {};
  //       next.specials[idx].image = url;
  //     }
  //     return next;
  //   });
  // };

  const handleAliasChange = e => {
    const v = e.target.value;
    setEditedLanding(prev => ({ ...prev, alias: v }));
  };

  const handleStartDateChange = (idx, date) => {
    setSpecialDates(sd => {
      const arr = [...sd];
      arr[idx] = { ...arr[idx], startDate: date };
      return arr;
    });
    setEditedLanding(prev => {
      const arr = prev.specials.map((s, i) =>
        i === idx ? { ...s, startDatetime: date } : s
      );
      return { ...prev, specials: arr };
    });
  };

  const handleEndDateChange = (idx, date) => {
    setSpecialDates(sd => {
      const arr = [...sd];
      arr[idx] = { ...arr[idx], endDate: date };
      return arr;
    });
    setEditedLanding(prev => {
      const arr = prev.specials.map((s, i) =>
        i === idx ? { ...s, endDatetime: date } : s
      );
      return { ...prev, specials: arr };
    });
  };

  const handleAddSpecial = () => {
    if (!editedLanding) return;
    const today = getToday();
    setEditedLanding(prev => ({
      ...prev,
      specials: [...prev.specials, { startDatetime: today, endDatetime: today, image: '' }]
    }));

    setSpecialDates(sd => [
      ...sd,
      { startDate: today, endDate: today }
    ]);
  };

  const handleSwitchChange = (checked) => {
    if (!editedLanding) return;
    setEditedLanding(prev => ({
      ...prev,
      validity: { ...(prev.validity || {}), isActive: checked }
    }));
  };


  const handleDeleteSpecial = idx => {
    if (!editedLanding) return;
    setEditedLanding(prev => ({
      ...prev,
      specials: prev.specials.filter((_, i) => i !== idx)
    }));
    setSpecialDates(sd => sd.filter((_, i) => i !== idx));
  };

  const handleMainImageChange = (url) => {
    setEditedLanding(prev => ({ ...prev, image: url }));
  };

  const handleSpecialImageChange = (idx, url) => {
    setEditedLanding(prev => {
      const specials = [...prev.specials];
      specials[idx] = { ...specials[idx], image: url };
      return { ...prev, specials };
    });
  };

  const handleSave = () => {
    if (!editedLanding) return;

    // Check whether any special is missing an image
    const hasEmptyImage = editedLanding.specials.some(s => !s.image || s.image.trim() === '');

    if (hasEmptyImage) {
      antdMessage.error('Please upload an image for each special before saving.');
      return;
    }

    // End date cannot be earlier than start date
    const hasInvalidRange = specialDates.some(d =>
      new Date(d.endDate).getTime() < new Date(d.startDate).getTime()
    );
    if (hasInvalidRange) {
      setMessage('End date cannot be earlier than start date.');
      antdMessage.error('End date cannot be earlier than start date.');
      return;
    }

    // Check whether any specials overlap
    const specialsWithDates = editedLanding.specials.map((s, idx) => ({
      idx,
      start: new Date(specialDates[idx].startDate).getTime(),
      end: new Date(specialDates[idx].endDate).getTime()
    }));

    let overlapFound = false;
    for (let i = 0; i < specialsWithDates.length; i++) {
      for (let j = i + 1; j < specialsWithDates.length; j++) {
        const si = specialsWithDates[i];
        const sj = specialsWithDates[j];
        if (si.start <= sj.end && sj.start <= si.end) {
          overlapFound = true;
          break;
        }
      }
      if (overlapFound) break;
    }

    if (overlapFound) {
      setMessage('Special dates cannot overlap.');
      antdMessage.error('Special dates cannot overlap.');
      return;
    }

    const sorted = [...editedLanding.specials].sort((a, b) =>
      toEndOfDay(a.endDatetime) - toEndOfDay(b.endDatetime)
    );
    const formatted = sorted.map(s => ({
      ...s,
      startDatetime: toStartOfDay(s.startDatetime),
      endDatetime: toEndOfDay(s.endDatetime)
    }));
    const body = {
      ...editedLanding,
      validity: {
        ...(editedLanding.validity || {}),
        isActive: editedLanding.validity?.isActive ?? false
      },
      specials: formatted
    };
    fetch(`${process.env.REACT_APP_API_BASE_URL}/private/adv/${selectedRole.id}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => window.location.reload())
      .catch(err => console.error(err));
  };

  // Data has not finished loading yet
  if (!initialLanding || !editedLanding) {
    return (
      <div className="w-[90%] max-w-7xl mx-auto py-20 text-center text-gray-500">
        Loading advertisement settings...
      </div>
    );
  }

  return (
    <div className="w-[90%] max-w-7xl mx-auto mb-20">
      <h1 className="text-2xl text-blue-600 font-semibold py-4">
        Advertisement Settings
      </h1>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 mb-4">
        <button
          onClick={() => setTab('image')}
          className={`px-4 py-2 ${tab === 'image'
            ? 'border-b-2 border-blue-500 text-blue-500'
            : 'text-gray-500'
            }`}
        >
          Main Image & Alias
        </button>
        <button
          onClick={() => setTab('specials')}
          className={`px-4 py-2 ${tab === 'specials'
            ? 'border-b-2 border-blue-500 text-blue-500'
            : 'text-gray-500'
            }`}
        >
          Specials
        </button>
      </div>

      {/* Image & Alias */}
      {tab === 'image' && (
        <>
          <div className="bg-white border border-gray-200 p-4 mb-4">
            <h2 className="font-bold mb-2">Main Image</h2>
            <div className="flex flex-wrap items-center space-x-4">
              <div className="w-full md:w-auto">
                {editedLanding.image
                  ? <img src={editedLanding.image} alt="Main" className="w-auto h-[20vh]" />
                  : <span>No Image Uploaded</span>
                }
              </div>
              <div className="flex items-center space-x-4">
                {/* <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleFileChange('image', e.target.files[0], 'image')}
                  className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <a href={editedLanding.image} download className="text-blue-500 hover:text-blue-700">
                  <FaDownload />
                </a> */}
                {/* Use the latest ImageUploader */}
                <ImageUploader
                  token={token}              // from AppContext
                  width={1920}               // crop width
                  height={1080}              // crop height (16:9)
                  objectFit="cover"
                  buttonText="Upload Image"
                  prefix={prefix}
                  thumbPrefix={thumbPrefix}
                  onChange={handleMainImageChange}
                />

                {editedLanding.image && (
                  <a
                    href={editedLanding.image}
                    download
                    className="text-blue-500 hover:text-blue-700"
                    title="Download current image"
                  >
                    <FaDownload />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-4">
            <h2 className="font-bold mb-2">Advertising Alias</h2>
            <InputWithCounter
              type="text"
              value={editedLanding.alias || ''}
              maxLength={64}
              onChange={handleAliasChange}
            />
          </div>

          <div className="bg-white border border-gray-200 p-4 mt-4">
            <h2 className="font-bold mb-2">Active</h2>
            <Switch
              checked={editedLanding.validity?.isActive || false}
              onChange={handleSwitchChange}
            />
          </div>

        </>
      )}

      {/* Specials */}
      {tab === 'specials' && (
        <div className="bg-white border border-gray-200 p-4 space-y-4 h-[75vh] overflow-y-auto">
          {message && <p className="text-red-500">{message}</p>}
          {editedLanding.specials.map((s, idx) => (
            <div key={idx} className="border-b border-gray-300 pb-4">
              <div className="flex flex-wrap items-center space-x-4 mb-4">
                <div className="w-8 text-center">
                  <span className="bg-blue-700 text-white rounded-full w-6 h-6 flex items-center justify-center">
                    {idx + 1}
                  </span>
                </div>
                <div className="flex-grow">
                  {s.image && <img src={s.image} alt="Special" className="h-24 mx-auto" />}
                </div>
                {/* <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleFileChange(`specials_${idx}_image`, e.target.files[0], 'specials')}
                  className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                /> */}
                <ImageUploader
                  token={token}
                  width={1920}
                  height={1080}
                  objectFit="cover"
                  buttonText="Upload Special Image"
                  prefix={prefix}
                  thumbPrefix={thumbPrefix}
                  backgroundColor="#ACACAC"
                  onChange={(url) => handleSpecialImageChange(idx, url)}

                />

                <button
                  onClick={() => handleDeleteSpecial(idx)}
                  className="text-red-500 hover:text-red-700"
                  title="Delete this special"
                >
                  <FaTrash />
                </button>
                <a href={s.image} download className="text-blue-500 hover:text-blue-700">
                  <FaDownload />
                </a>
              </div>
              <div className="flex space-x-4">
                <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={specialDates[idx]?.startDate}
                    min={getToday()}
                    onChange={e => handleStartDateChange(idx, e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={specialDates[idx]?.endDate}
                    min={specialDates[idx]?.startDate}
                    onChange={e => handleEndDateChange(idx, e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />

                </div>
              </div>
            </div>
          ))}
          <div className="text-center">
            {editedLanding.specials.length < 6 ? (
              <button
                onClick={handleAddSpecial}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                <FaPlus className="mr-2" /> Add New Special
              </button>
            ) : (
              <p className="text-red-500">You have reached the maximum of 6 specials.</p>
            )}
          </div>
        </div>
      )}

      {/* Footer & Modal */}
      <div className="text-right mt-4">
        <FloatingFooter hasChanges={hasChanges} onSave={() => setIsModalOpen(true)} />
        <ConfirmModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleSave}
          header="Confirm Save"
          content="Are you sure you want to save changes?"
        />
      </div>
    </div>
  );
};

export default AdsSettings;


